import { clsx } from "@trydig/ui";
import type { ComponentProps } from "react";
import { Asset } from "@/components/ui/Asset";
import { Container } from "@/components/ui/Container";
import { ProductCard } from "@/components/ui/ProductCard";
import { FeaturedCollectionCarousel } from "@/components/ui/sections/_lib/FeaturedCollectionCarousel";
import { Typography } from "@/components/ui/Typography";
import type { ProductsRoute } from "@/data/routes/products.route";
import type { AssetStoryblok } from "@/generated/sb/sb-types";
import { isDarkMode } from "@/lib/utils/isDarkMode";
import { Reveal } from "../Reveal";

export interface FeaturedCollectionProductsProps extends ComponentProps<"section"> {
	caption?: string;
	heading?: string;
	description?: string;
	asset?: AssetStoryblok;
	products?: ProductsRoute["exploration"]["products"];
	bg?: string;
}

const Heading = ({
	caption,
	heading,
	description,
	darkMode,
}: {
	caption?: string;
	heading?: string;
	description?: string;
	darkMode?: boolean;
}) => {
	if (!caption && !heading && !description) return null;
	return (
		<div
			className={clsx(
				"flex w-full flex-col gap-grid-component-spacing-md text-text-dark-text-1",
				darkMode && "text-text-light-text-1",
			)}
		>
			{caption && (
				<Typography variant="label-sm-reg" as="p" className="leading-[1.15]">
					{caption}
				</Typography>
			)}
			{heading && (
				<Typography variant="heading-display-md" as="h2">
					{heading}
				</Typography>
			)}
			{description && (
				<Typography variant="body-sm-reg" as="p">
					{description}
				</Typography>
			)}
		</div>
	);
};

/**
 * FeaturedCollectionProducts — image on the left, heading + product grid /
 * carousel on the right, framed with horizontal dividers. Mobile stacks
 * heading → divider → image → product carousel.
 */
export const FeaturedCollectionProducts = ({
	caption,
	heading,
	description,
	asset,
	products,
	className,
	bg,
	...rest
}: FeaturedCollectionProductsProps) => {
	const darkMode = isDarkMode(bg);
	const items = (products ?? []).map((product) => (
		<ProductCard key={product.id} product={product} size="medium" darkMode={darkMode} />
	));

	const desktopCarousel = items.length > 3;

	return (
		<section {...rest} style={{ backgroundColor: bg ? bg : undefined }}>
			<Container className="py-grid-section-vertical-lg">
				<div className={clsx("flex w-full flex-col gap-grid-margin-sm", className)}>
					<div className="md:hidden">
						<Heading
							caption={caption}
							heading={heading}
							description={description}
							darkMode={darkMode}
						/>
					</div>

					<Reveal animation="draw-left" delay={100}>
						<div
							className={clsx("h-px w-full bg-border-dark", darkMode && "bg-border-light")}
							aria-hidden
						/>
					</Reveal>

					<div className="flex w-full flex-col gap-grid-margin-sm md:flex-row md:items-start md:gap-grid-grid-gutter-lg md:overflow-hidden">
						{asset && (
							<div className="relative w-full md:min-w-px md:flex-1 bg-background-bg-image overflow-hidden aspect-square md:aspect-3/4">
								<Asset asset={asset} sizes="(max-width: 768px) 100vw, 50vw" />
							</div>
						)}

						<div
							className={clsx(
								"hidden md:block w-px self-stretch bg-border-dark",
								darkMode && "bg-border-light",
							)}
							aria-hidden
						/>

						<div className="hidden md:flex md:min-w-[300px] md:flex-1 md:flex-col md:items-start md:justify-between md:self-stretch md:gap-grid-section-vertical-md">
							<Heading
								caption={caption}
								heading={heading}
								description={description}
								darkMode={darkMode}
							/>
							{items.length > 0 &&
								(desktopCarousel ? (
									<FeaturedCollectionCarousel items={items} alwaysActive className="w-full" />
								) : (
									<div className="grid w-full grid-cols-3 gap-grid-grid-gutter-md">{items}</div>
								))}
						</div>

						{items.length > 0 && (
							<div className="md:hidden">
								<FeaturedCollectionCarousel items={items} alwaysActive bleedMobile />
							</div>
						)}
					</div>

					<Reveal animation="draw-right" delay={100}>
						<div
							className={clsx(
								"hidden md:block h-px w-full bg-border-dark",
								darkMode && "bg-border-light",
							)}
							aria-hidden
						/>
					</Reveal>
				</div>
			</Container>
		</section>
	);
};
